import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils/axiosError';
import EatingWayTimeFrameModal, { EatingWayTimeFrameModalProps } from '../components/EatingWayTimeFrameModal';
import SaleTypeDetailModal from '../components/SaleTypeDetailModal';
import { SaleType } from '../models';
import { TimeFrame } from '../models/TimeFrame';
import saleTypeService from '../saleTypeService';
import './SaleTypeListPage.scss';

const SEARCH_SHOW_DELETE_KEY_DEFAULT = '0';
interface FormSearchValueProps {
  showFlg: '0' | '1' | '2';
}

function SaleTypeListPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [showSaleTypeDetailModal, setShowSaleTypeDetailModal] = useState(false);

  const [saleTypeList, setSaleTypeList] = useState<SaleType[]>([]);
  const [selectedSaleType, setSelectedSaleType] = useState<SaleType>();

  // const [showTimeFrameMenuBookModal, setShowTimeFrameMenuBookModal] = useState(false);
  // const [timeframeMenuBookModalData, setTimeframeMenuBookModalData] = useState<TimeFrameMenuBookModalData>();
  const [showEatingWayTimeFrameModal, setShowEatingWayTimeFrameModal] = useState(false);
  const [eatingWayTimeFrameModalData, setEatingWayTimeFrameModalData] = useState<EatingWayTimeFrameModalProps>();

  // ref
  const selectedRowIndexRef = useRef<number[]>([]);
  const btnViewDetailRef = useRef<HTMLButtonElement>(null);
  const btnCopyRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);

  // search
  const formSearchRef = useRef<HTMLFormElement>(null);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    showFlg: '1',
  });

  const SHOW_STATUS_SOURCE: { [key: string]: string } = {
    '0': t('MenuBookListPage.show_valid'),
    '1': t('MenuBookListPage.show_all'),
    '2': t('MenuBookListPage.show_deleted'),
  };

  async function loadSaleTypeDataList() {
    if (formSearchRef.current) {
      const showFlg = Number(formSearchRef.current['show_flg'].value.trim());
      // const showFlg = 1;

      try {
        const response = await saleTypeService.getAll(showFlg);
        setSaleTypeList(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    }
  }

  useEffect(() => {
    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }, []);

  useEffect(() => {
    //call API service to get data
    loadSaleTypeDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOpenSaleTypeDetailModal() {
    setSelectedSaleType(undefined);
    setShowSaleTypeDetailModal(true);
  }

  function handleCloseSaleTypeDetailModal(isDataChanged: boolean = false) {
    handleUnSelectAll();
    setShowSaleTypeDetailModal(false);
    setSelectedSaleType(undefined);

    if (isDataChanged) {
      loadSaleTypeDataList();
    }
  }

  // function handleOpenTimeFrameMenuBookModal(saleType: SaleType, timeframe: TimeFrame) {
  //   const modalData: TimeFrameMenuBookModalData = {
  //     saletype_id: saleType.id || 0,
  //     saletype_name: saleType.name,
  //     timeframe_id: timeframe.id || 0,
  //     timeframe_name: timeframe.name,
  //   };
  //   setTimeframeMenuBookModalData(modalData);
  //   setShowTimeFrameMenuBookModal(true);
  // }

  // function handleCloseTimeFrameMenuBookModal(isDataChanged: boolean) {
  //   handleUnSelectAll();
  //   setShowTimeFrameMenuBookModal(false);

  //   if (isDataChanged) {
  //     loadSaleTypeDataList();
  //   }
  // }

  function handleOpenEatingWayTimeFrameModal(saleType: SaleType, timeFrame: TimeFrame) {
    const modalData: EatingWayTimeFrameModalProps = {
      timeFrame,
      saleType,
    };
    setEatingWayTimeFrameModalData(modalData);
    setShowEatingWayTimeFrameModal(true);
  }

  function handleCloseEatingWayTimeFrameModal() {
    handleUnSelectAll();
    setShowEatingWayTimeFrameModal(false);
  }

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('input[name="selected_saletype"]:checked');
    chkSelectionElems.forEach(function (elem) {
      elem.checked = false;
    });

    selectedRowIndexRef.current = [];

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }

  function handleSelectSaleType(e: React.ChangeEvent<HTMLInputElement>) {
    const elements = document.querySelectorAll<HTMLInputElement>('input[name="selected_saletype"]:checked');

    const selectedRowIndexs: number[] = [];
    elements.forEach(function (element) {
      selectedRowIndexs.push(Number(element.value));
    });
    selectedRowIndexRef.current = selectedRowIndexs;

    if (btnViewDetailRef.current && btnCopyRef.current && btnDeleteRef.current) {
      if (elements.length === 0) {
        btnViewDetailRef.current.disabled = true;
        btnCopyRef.current.disabled = true;
        btnDeleteRef.current.disabled = true;
      } else if (elements.length === 1) {
        btnViewDetailRef.current.disabled = false;
        btnCopyRef.current.disabled = false;
        btnDeleteRef.current.disabled = false;
      } else {
        btnViewDetailRef.current.disabled = true;
        btnCopyRef.current.disabled = true;
        btnDeleteRef.current.disabled = false;
      }
    }
  }

  function handleShowDetail() {
    if (selectedRowIndexRef.current.length === 0 || selectedRowIndexRef.current.length > 1) {
      return;
    }

    const selectedSaleTypeId = selectedRowIndexRef.current[0];
    const saleType = saleTypeList.find((item) => item.id === selectedSaleTypeId);
    if (saleType) {
      const cloneSaleType = JSON.parse(JSON.stringify(saleType)) as SaleType;
      setSelectedSaleType(cloneSaleType);
      setShowSaleTypeDetailModal(true);
    }
  }

  function handleCopy() {
    if (selectedRowIndexRef.current.length === 0 || selectedRowIndexRef.current.length > 1) {
      return;
    }

    const selectedSaleTypeId = selectedRowIndexRef.current[0];
    const saleType = saleTypeList.find((item) => item.id === selectedSaleTypeId);
    if (saleType) {
      const cloneSaleType = JSON.parse(JSON.stringify(saleType)) as SaleType;
      delete cloneSaleType.id;
      delete cloneSaleType.delete_flg;
      cloneSaleType.timeframes = cloneSaleType.timeframes.filter((item) => !item.delete_flg);
      cloneSaleType.timeframes.forEach((timeframe) => {
        timeframe.copyFromTimeFrameId = timeframe.id;
        delete timeframe.id;
        delete timeframe.sale_type_id;
        // delete timeframe.delete_flg;
      });
      setSelectedSaleType(cloneSaleType);
      setShowSaleTypeDetailModal(true);
    }
  }

  async function processDeleteSaleType() {
    try {
      const response = await saleTypeService.delete({ ids: selectedRowIndexRef.current });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadSaleTypeDataList();
      handleUnSelectAll();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleDelete() {
    if (selectedRowIndexRef.current.length === 0) {
      return;
    }

    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('SaleTypeListPage.warn_confirm_delete')}
        onConfirm={processDeleteSaleType}
      />
    ));
  }

  function saveFormSearch() {
    if (formSearchRef.current) {
      setFormSearchValue({
        ...formSearchValue,
        showFlg: formSearchRef.current['show_flg'].value.trim(),
      });
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    handleUnSelectAll();
    loadSaleTypeDataList();
  }

  async function processRestore(id: number) {
    try {
      const response = await saleTypeService.restore({ ids: [id] });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadSaleTypeDataList();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleRestore(id: number) {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('SaleTypeListPage.warn_confirm_restore')}
        onConfirm={processRestore.bind(null, id)}
      />
    ));
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <Row className="mb-3">
            <Col sm={6}>
              <FeatureTitleBar title={t('SaleTypeListPage.head_title')} />
            </Col>
            <Col sm={6} className="text-end">
              <Button className="btn--dark-green" onClick={handleOpenSaleTypeDetailModal}>
                {t('SaleTypeListPage.add_new')}
              </Button>
            </Col>
          </Row>
          <form ref={formSearchRef} className="mb-3" onSubmit={handleSubmit}>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{t('MenuBookListPage.search')}</Accordion.Header>
                <Accordion.Body onExited={saveFormSearch}>
                  <Row>
                    <Col sm={9}>
                      <Row className="mb-0">
                        <Col sm={'auto'} className="text-end">
                          <Form.Label className="col-form-label">{t('MenuBookListPage.delete')}:</Form.Label>
                        </Col>
                        <Col sm={'auto'}>
                          {Object.keys(SHOW_STATUS_SOURCE).map(function (key) {
                            return (
                              <Form.Check
                                key={`show-status-${key}`}
                                className="col-form-label1 form-label1 py-1"
                                type="radio"
                                name="show_flg"
                                id={`show-status-${key}`}
                                value={key}
                                defaultChecked={key === SEARCH_SHOW_DELETE_KEY_DEFAULT}
                                label={SHOW_STATUS_SOURCE[key]}
                              />
                            );
                          })}
                        </Col>
                      </Row>
                    </Col>
                    <Col sm={3} className="text-end">
                      <Button type="submit">{t('MenuBookListPage.search')}</Button>
                    </Col>
                  </Row>
                </Accordion.Body>
                <div className="accordion-body accordion-summary">
                  <Row>
                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('MenuBookListPage.delete')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {SHOW_STATUS_SOURCE[formSearchValue.showFlg] || '-'}
                      </Form.Label>
                    </Col>
                  </Row>
                </div>
              </Accordion.Item>
            </Accordion>
          </form>
          <div className="text-end mb-2">
            <Button ref={btnViewDetailRef} className="ms-3" onClick={handleShowDetail}>
              {t('SaleTypeListPage.detailed')}
            </Button>
            <Button ref={btnCopyRef} className="ms-3" onClick={handleCopy}>
              {t('SaleTypeListPage.copy')}
            </Button>
            <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
              {t('SaleTypeListPage.delete')}
            </Button>
          </div>
          <div className="saletype-list-table sticky-table flex-grow-1 flex-shrink-1">
            <Table hover>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: '4%' }}>
                    {t('SaleTypeListPage.selection')}
                  </th>
                  <th rowSpan={2} style={{ width: '24%' }}>
                    {t('SaleTypeListPage.sale_type_name')}
                  </th>
                  <th colSpan={4} style={{ width: '60%' }}>
                    {t('SaleTypeListPage.timeframe')}
                  </th>
                  <th rowSpan={2} style={{ width: '12%' }}>
                    {t('SaleTypeListPage.eating_way_selection')}
                  </th>
                </tr>
                <tr>
                  <th style={{ width: '15%' }}>{t('SaleTypeListPage.timeframe_name')}</th>
                  <th style={{ width: '15%' }}>{t('SaleTypeListPage.timeframe_time')}</th>
                  <th style={{ width: '15%' }}>{t('SaleTypeListPage.timeframe_holiday_time')}</th>
                  <th style={{ width: '15%' }}>{t('SaleTypeListPage.timeframe_busy_time')}</th>
                </tr>
              </thead>
              <tbody>
                {saleTypeList.map(function (saleType, saleTypeIndex) {
                  const timeframes = saleType.timeframes.filter((x) => !x.delete_flg);
                  return (
                    <React.Fragment key={saleType.id}>
                      <tr
                        className={clsx([
                          'divide',
                          { 'bg-light': saleType.delete_flg },
                          { 'last-row': saleTypeIndex === saleTypeList.length - 1 },
                        ])}
                      >
                        <td rowSpan={timeframes.length === 0 ? 2 : timeframes.length + 1} className="text-center">
                          {saleType.delete_flg && (
                            <Button className="btn--orange" onClick={handleRestore.bind(null, Number(saleType.id))}>
                              {t('SaleTypeListPage.restore')}
                            </Button>
                          )}
                          {!saleType.delete_flg && (
                            <Form.Check
                              name="selected_saletype"
                              className="custom-checkbox"
                              value={saleType.id}
                              onChange={handleSelectSaleType}
                            ></Form.Check>
                          )}
                        </td>
                        <td rowSpan={timeframes.length === 0 ? 2 : timeframes.length + 1} className="text-center">
                          {saleType.name}
                        </td>
                      </tr>

                      {timeframes.length === 0 && (
                        <tr className="divide">
                          <td className="text-center"></td>
                          <td className="text-center"></td>
                          <td className="text-center"></td>
                          <td className="text-center"></td>
                          <td className="wrap"></td>
                        </tr>
                      )}

                      {timeframes.length > 0 &&
                        timeframes.map(function (timeframe, timeframeIndex) {
                          return (
                            <tr
                              key={saleType.id + '-' + timeframe.id}
                              className={clsx(
                                { divide: saleTypeIndex > 0 && timeframeIndex === 0 },
                                { 'bg-light': saleType.delete_flg }
                              )}
                            >
                              <td className="text-center">{timeframe.name}</td>
                              <td className="text-center">
                                {timeframe.start ? (
                                  <span>
                                    {timeframe.start} ～ {timeframe.end}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="text-center">
                                {timeframe.holiday_start ? (
                                  <span>
                                    {timeframe.holiday_start} ～ {timeframe.holiday_end}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="text-center">
                                {timeframe.busy_time_start ? (
                                  <span>
                                    {timeframe.busy_time_start} ～ {timeframe.busy_time_end}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="wrap text-center">
                                {!saleType.delete_flg && (
                                  <Button
                                    className="btn--dark-green flex-grow-0 flex-shrink-0"
                                    dangerouslySetInnerHTML={{ __html: t('SaleTypeListPage.menu_book_edit') }}
                                    onClick={handleOpenEatingWayTimeFrameModal.bind(null, saleType, timeframe)}
                                  ></Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {showSaleTypeDetailModal && (
        <SaleTypeDetailModal saleType={selectedSaleType} onClose={handleCloseSaleTypeDetailModal} />
      )}
      {showEatingWayTimeFrameModal && eatingWayTimeFrameModalData && (
        <EatingWayTimeFrameModal
          timeFrame={eatingWayTimeFrameModalData.timeFrame}
          saleType={eatingWayTimeFrameModalData.saleType}
          onClose={handleCloseEatingWayTimeFrameModal}
        />
      )}
    </div>
  );
}

export default SaleTypeListPage;
